if (!customElements.get('etsy-framed-text-customizer')) {
  class EtsyFramedTextCustomizer extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.initialize();
    }

    async initialize() {
      this.textInput = this.querySelector('.framed-text__input');
      this.sizeInput = this.querySelector('.framed-text__size-range');
      this.rotationInput = this.querySelector('.framed-text__rotation-range');
      this.tiltInput = this.querySelector('.framed-text__tilt-range');
      this.characterCount = this.querySelector('.ice-customizer__character-count');
      this.error = this.querySelector('.ice-customizer__error');
      this.viewer = this.querySelector('.ice-customizer__viewer');
      this.canvas = this.querySelector('.ice-customizer__canvas');
      this.dragHint = this.querySelector('.ice-customizer__drag-hint');
      this.viewControls = this.querySelector('.ice-customizer__view-controls');
      this.viewButtons = [...this.querySelectorAll('[data-ice-view]')];
      this.yaw = Math.PI / 4;
      this.pitch = -0.08;
      this.cameraView = '3d';
      this.dragStart = null;
      this.currentText = '';
      this.updateTimer = null;

      this.bindEvents();
      this.updateCharacterCount();

      try {
        const engine = await this.waitForPreviewEngine();
        this.THREE = engine;
        this.SVGLoader = engine.SVGLoader;
        this.svgLoader = new this.SVGLoader();
        this.opentype = engine.opentype;
        if (!this.opentype?.parse) throw new Error('Font processor did not load');
        this.font = this.loadEmbeddedFont(this.dataset.fontBase64);
        this.setupScene();
        this.resizeRenderer();
        this.rebuildText();
      } catch (error) {
        console.error('Framed text preview could not start:', error);
        const reason = error instanceof Error && error.message ? ` (${error.message})` : '';
        this.showError(`The framed text preview could not load${reason}. Please refresh and try again.`);
      }
    }

    waitForPreviewEngine() {
      if (window.CustomIcePreviewEngine) return Promise.resolve(window.CustomIcePreviewEngine);
      return new Promise((resolve, reject) => {
        let attempts = 0;
        const timer = window.setInterval(() => {
          attempts += 1;
          if (window.CustomIcePreviewEngine) {
            window.clearInterval(timer);
            resolve(window.CustomIcePreviewEngine);
          } else if (attempts >= 120) {
            window.clearInterval(timer);
            reject(new Error('Preview engine did not load'));
          }
        }, 50);
      });
    }

    loadEmbeddedFont(encoded) {
      if (!encoded) throw new Error('Embedded Tinkercad font data is missing');
      const binary = window.atob(encoded);
      const bytes = new Uint8Array(binary.length);
      for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
      return this.opentype.parse(bytes.buffer);
    }

    bindEvents() {
      this.textInput.addEventListener('input', () => {
        this.updateCharacterCount();
        window.clearTimeout(this.updateTimer);
        this.updateTimer = window.setTimeout(() => this.rebuildText(), 80);
      });
      this.sizeInput.addEventListener('input', () => this.rebuildText());
      this.rotationInput.addEventListener('input', () => {
        if (!this.sceneReady) return;
        if (this.cameraView === 'top') {
          this.cameraView = '3d';
          this.pitch = -0.08;
          this.updateViewButtons();
          this.frameCamera();
        }
        this.yaw = this.THREE.MathUtils.degToRad(Number(this.rotationInput.value));
        this.updateModelRotation();
        this.render();
      });
      this.tiltInput.addEventListener('input', () => {
        if (!this.sceneReady) return;
        if (this.cameraView === 'top') {
          this.cameraView = '3d';
          this.updateViewButtons();
          this.frameCamera();
        }
        this.pitch = this.THREE.MathUtils.degToRad(Number(this.tiltInput.value));
        this.updateModelRotation();
        this.render();
      });
      this.viewButtons.forEach((button) => button.addEventListener('click', () => this.setView(button.dataset.iceView)));

      this.viewer.addEventListener('pointerdown', (event) => {
        if (event.target.closest('.ice-customizer__view-controls') || !this.sceneReady) return;
        event.preventDefault();
        if (this.cameraView === 'top') {
          this.cameraView = '3d';
          this.updateViewButtons();
          this.frameCamera();
        }
        this.dragStart = { x: event.clientX, y: event.clientY, yaw: this.yaw, pitch: this.pitch, width: Math.max(1, this.viewer.clientWidth), height: Math.max(1, this.viewer.clientHeight) };
        this.activePointerId = event.pointerId;
        try { this.viewer.setPointerCapture(event.pointerId); } catch (error) { /* Document listeners continue the drag. */ }
      });
      this.documentPointerMoveHandler = (event) => {
        if (!this.dragStart || event.pointerId !== this.activePointerId) return;
        event.preventDefault();
        const horizontalTravel = (event.clientX - this.dragStart.x) / this.dragStart.width;
        const verticalTravel = (event.clientY - this.dragStart.y) / this.dragStart.height;
        this.yaw = this.dragStart.yaw + horizontalTravel * Math.PI * 2;
        this.pitch = this.dragStart.pitch + verticalTravel * Math.PI * 2;
        this.updateModelRotation();
        this.syncRotationSliders();
        this.render();
      };
      this.documentPointerEndHandler = (event) => {
        if (event.pointerId !== this.activePointerId) return;
        this.dragStart = null;
        this.activePointerId = null;
      };
      document.addEventListener('pointermove', this.documentPointerMoveHandler, { passive: false });
      document.addEventListener('pointerup', this.documentPointerEndHandler);
      document.addEventListener('pointercancel', this.documentPointerEndHandler);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.resizeRenderer());
        this.resizeObserver.observe(this.viewer);
      }
    }

    updateCharacterCount() {
      if (this.characterCount) this.characterCount.textContent = `${[...this.textInput.value].length} / 16`;
    }

    setupScene() {
      const THREE = this.THREE;
      this.renderer = new THREE.WebGLRenderer({
        canvas: this.canvas,
        antialias: true,
        alpha: false,
        preserveDrawingBuffer: true,
        powerPreference: 'high-performance'
      });
      this.renderer.setClearColor(0x000000, 1);
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      this.renderer.outputColorSpace = THREE.SRGBColorSpace;
      this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
      this.renderer.toneMappingExposure = 1.18;
      this.renderer.shadowMap.enabled = false;

      this.scene = new THREE.Scene();
      this.scene.background = new THREE.Color(0x000000);
      this.camera = new THREE.PerspectiveCamera(31, 1, 1, 600);
      this.scene.add(new THREE.HemisphereLight(0xe8f9ff, 0x071018, 2.25));

      const key = new THREE.DirectionalLight(0xffffff, 4.3);
      key.position.set(-70, 125, 80);
      this.scene.add(key);
      const rim = new THREE.DirectionalLight(0x8cdfff, 3.5);
      rim.position.set(90, 70, -85);
      this.scene.add(rim);
      const front = new THREE.PointLight(0xd9f7ff, 75, 280, 2);
      front.position.set(0, 45, 115);
      this.scene.add(front);

      this.model = new THREE.Group();
      this.model.rotation.order = 'YXZ';
      this.scene.add(this.model);

      const baseGeometry = this.parseBinaryStl(this.dataset.modelBase64);
      baseGeometry.rotateX(-Math.PI / 2);
      baseGeometry.computeBoundingBox();
      const center = baseGeometry.boundingBox.getCenter(new THREE.Vector3());
      baseGeometry.translate(-center.x, -center.y, -center.z);
      baseGeometry.computeVertexNormals();

      const iceMaterial = new THREE.MeshPhysicalMaterial({
        color: 0xbcefff,
        transparent: true,
        opacity: 0.38,
        transmission: 0.55,
        thickness: 8,
        ior: 1.31,
        roughness: 0.12,
        metalness: 0,
        clearcoat: 0.35,
        clearcoatRoughness: 0.15,
        side: THREE.DoubleSide,
        depthWrite: false
      });
      this.baseMesh = new THREE.Mesh(baseGeometry, iceMaterial);
      this.baseMesh.renderOrder = 1;
      this.model.add(this.baseMesh);

      const edges = new THREE.EdgesGeometry(baseGeometry, 18);
      const edgeMaterial = new THREE.LineBasicMaterial({ color: 0xdff9ff, transparent: true, opacity: 0.5 });
      this.baseEdges = new THREE.LineSegments(edges, edgeMaterial);
      this.baseEdges.renderOrder = 2;
      this.model.add(this.baseEdges);

      this.updateModelRotation();
      this.sceneReady = true;
      this.dragHint.hidden = false;
      this.viewControls.hidden = false;
    }

    parseBinaryStl(encoded) {
      if (!encoded) throw new Error('Framed ice model data is missing');
      const binary = window.atob(encoded);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
      const view = new DataView(bytes.buffer);
      const triangleCount = view.getUint32(80, true);
      if (84 + triangleCount * 50 !== bytes.byteLength) throw new Error('Framed ice model data is invalid');
      const positions = new Float32Array(triangleCount * 9);
      let target = 0;
      for (let triangle = 0; triangle < triangleCount; triangle += 1) {
        const offset = 84 + triangle * 50 + 12;
        for (let vertex = 0; vertex < 3; vertex += 1) {
          const vertexOffset = offset + vertex * 12;
          positions[target++] = view.getFloat32(vertexOffset, true);
          positions[target++] = view.getFloat32(vertexOffset + 4, true);
          positions[target++] = view.getFloat32(vertexOffset + 8, true);
        }
      }
      const geometry = new this.THREE.BufferGeometry();
      geometry.setAttribute('position', new this.THREE.Float32BufferAttribute(positions, 3));
      return geometry;
    }

    rebuildText() {
      if (!this.sceneReady || !this.font) return;
      const enteredText = this.textInput.value.trim().replace(/\s+/g, ' ');
      this.currentText = enteredText;
      this.disposeRelief();
      if (!enteredText) {
        this.hideError();
        this.render();
        return;
      }

      try {
        const path = this.font.getPath(enteredText, 0, 0, 100, { kerning: true });
        const pathData = path.toPathData(3);
        const pathBounds = path.getBoundingBox();
        const sourceWidth = pathBounds.x2 - pathBounds.x1;
        const sourceHeight = pathBounds.y2 - pathBounds.y1;
        if (!pathData || !Number.isFinite(sourceWidth) || sourceWidth <= 0 || sourceHeight <= 0) {
          throw new Error('Enter text that contains visible characters.');
        }

        const svg = `<svg xmlns="http://www.w3.org/2000/svg"><path fill="#000" fill-rule="evenodd" d="${pathData}"/></svg>`;
        const parsed = this.svgLoader.parse(svg);
        const shapes = [];
        parsed.paths.forEach((svgPath) => this.SVGLoader.createShapes(svgPath).forEach((shape) => shapes.push(shape)));
        if (!shapes.length) throw new Error('The entered text could not be converted into a preview.');

        const geometries = shapes.map((shape) => {
          const geometry = new this.THREE.ExtrudeGeometry(shape, { depth: 4.5, bevelEnabled: false, curveSegments: 24, steps: 1 });
          geometry.computeBoundingBox();
          return geometry;
        });
        const bounds = new this.THREE.Box3();
        geometries.forEach((geometry) => bounds.union(geometry.boundingBox));
        const width = bounds.max.x - bounds.min.x;
        const height = bounds.max.y - bounds.min.y;
        if (!Number.isFinite(width) || width <= 0 || height <= 0) throw new Error('The entered text has no usable outline.');

        const requestedFraction = Number(this.sizeInput.value || 100) / 100;
        const fitScale = Math.min((36 * requestedFraction) / width, 32 / height);
        const centerX = (bounds.min.x + bounds.max.x) / 2;
        const centerY = (bounds.min.y + bounds.max.y) / 2;
        const material = new this.THREE.MeshPhysicalMaterial({
          color: 0xdaf8ff,
          transparent: true,
          opacity: 0.94,
          transmission: 0.12,
          thickness: 1.5,
          ior: 1.31,
          roughness: 0.14,
          metalness: 0,
          clearcoat: 0.45,
          clearcoatRoughness: 0.14,
          side: this.THREE.DoubleSide,
          depthWrite: true
        });
        const relief = new this.THREE.Group();
        geometries.forEach((geometry) => {
          geometry.translate(-centerX, -centerY, 0);
          geometry.scale(fitScale, -fitScale, 1);
          geometry.rotateX(-Math.PI / 2);
          geometry.translate(0, 26.03, 0);
          geometry.computeVertexNormals();
          const mesh = new this.THREE.Mesh(geometry, material);
          mesh.renderOrder = 3;
          relief.add(mesh);
        });
        this.relief = relief;
        this.reliefMaterial = material;
        this.model.add(relief);
        this.hideError();
        this.render();
      } catch (error) {
        console.error('Framed text conversion failed:', error);
        this.showError(error.message || 'The text could not be converted into a preview.');
        this.render();
      }
    }

    disposeRelief() {
      if (!this.relief) return;
      this.model.remove(this.relief);
      this.relief.traverse((child) => child.geometry?.dispose());
      this.reliefMaterial?.dispose();
      this.relief = null;
      this.reliefMaterial = null;
    }

    updateModelRotation() {
      if (!this.model) return;
      this.model.rotation.y = this.yaw;
      this.model.rotation.x = this.pitch;
    }

    syncRotationSliders() {
      const yawDegrees = ((this.yaw * 180 / Math.PI) % 360 + 360) % 360;
      const pitchDegrees = ((this.pitch * 180 / Math.PI) % 360 + 360) % 360;
      if (this.rotationInput) this.rotationInput.value = String(Math.round(yawDegrees));
      if (this.tiltInput) this.tiltInput.value = String(Math.round(pitchDegrees));
    }

    setView(view) {
      if (!this.camera || !this.model) return;
      if (view === 'top') {
        this.cameraView = 'top';
        this.yaw = 0;
        this.pitch = 0;
      } else {
        this.cameraView = '3d';
        this.yaw = Math.PI / 4;
        this.pitch = -0.08;
      }
      this.updateModelRotation();
      this.syncRotationSliders();
      this.updateViewButtons();
      this.frameCamera();
      this.render();
    }

    updateViewButtons() {
      this.viewButtons.forEach((button) => {
        const active = button.dataset.iceView === this.cameraView;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    resizeRenderer() {
      if (!this.renderer || !this.camera) return;
      const rect = this.viewer.getBoundingClientRect();
      const width = Math.max(1, Math.round(rect.width));
      const height = Math.max(1, Math.round(rect.height));
      this.renderer.setSize(width, height, false);
      this.camera.aspect = width / height;
      this.frameCamera();
      this.camera.updateProjectionMatrix();
      this.render();
    }

    frameCamera() {
      if (!this.camera || !this.THREE) return;
      const verticalFov = this.THREE.MathUtils.degToRad(this.camera.fov);
      const horizontalFov = 2 * Math.atan(Math.tan(verticalFov / 2) * this.camera.aspect);
      const limitingFov = Math.min(verticalFov, horizontalFov);
      const distance = (54 / Math.sin(limitingFov / 2)) * 1.08;
      const target = new this.THREE.Vector3(0, 0, 0);
      const direction = this.cameraView === 'top'
        ? new this.THREE.Vector3(0, 1, 0)
        : new this.THREE.Vector3(1.05, 0.92, 1.35).normalize();
      this.camera.up.set(0, this.cameraView === 'top' ? 0 : 1, this.cameraView === 'top' ? -1 : 0);
      this.camera.position.copy(target).addScaledVector(direction, distance);
      this.camera.lookAt(target);
      this.camera.updateProjectionMatrix();
    }

    render() {
      if (this.renderer && this.scene && this.camera) this.renderer.render(this.scene, this.camera);
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = false;
    }

    hideError() {
      if (!this.error) return;
      this.error.textContent = '';
      this.error.hidden = true;
    }

    disconnectedCallback() {
      window.clearTimeout(this.updateTimer);
      this.resizeObserver?.disconnect();
      if (this.documentPointerMoveHandler) document.removeEventListener('pointermove', this.documentPointerMoveHandler);
      if (this.documentPointerEndHandler) {
        document.removeEventListener('pointerup', this.documentPointerEndHandler);
        document.removeEventListener('pointercancel', this.documentPointerEndHandler);
      }
      this.disposeRelief();
      this.renderer?.dispose();
    }
  }

  customElements.define('etsy-framed-text-customizer', EtsyFramedTextCustomizer);
}
